/**
 * Model Config Management UI
 * View, add, edit, and manage AI model registry
 */

import { BaseComponent } from './components/base-component';
import type { APIClient } from './api';
import type { ModelConfig } from './types';

export class ModelConfigUI extends BaseComponent {
  private apiClient: APIClient;
  private models: ModelConfig[] = [];
  private filteredModels: ModelConfig[] = [];
  private loading = false;
  private editingModel: ModelConfig | null = null;

  constructor(container: HTMLElement, apiClient: APIClient) {
    super(container);
    this.apiClient = apiClient;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="model-config-ui">
        <div class="model-config-header">
          <h2>🤖 Model Registry Configuration</h2>
          <p class="subtitle">Manage AI models (Global configuration affects all users)</p>
        </div>

        <div class="model-config-toolbar">
          <div class="toolbar-left">
            <input
              type="text"
              id="model-search"
              placeholder="Search models (ID, provider, name)..."
              class="search-input"
            />
            <select id="provider-filter" class="filter-select">
              <option value="">All Providers</option>
              <option value="unified">Unified (Azure/Vertex)</option>
              <option value="deepseek">DeepSeek</option>
              <option value="bedrock">AWS Bedrock</option>
              <option value="workers-ai">Cloudflare Workers AI</option>
            </select>
          </div>
          <div class="toolbar-right">
            <button id="refresh-btn" class="btn btn-secondary">
              <span>🔄</span> Refresh
            </button>
            <button id="add-model-btn" class="btn btn-primary">
              <span>➕</span> Add Model
            </button>
          </div>
        </div>

        <div id="models-container" class="models-container">
          <div class="loading">Loading models...</div>
        </div>

        <div id="model-form-modal" class="modal" style="display: none;">
          <div class="modal-content">
            <div class="modal-header">
              <h3 id="modal-title">Add Model</h3>
              <button id="modal-close" class="close-btn">&times;</button>
            </div>
            <form id="model-form" class="model-form">
              <!-- Form fields will be injected -->
            </form>
          </div>
        </div>
      </div>
    `;

    this.setupEventListeners();
    await this.loadModels();
  }

  private setupEventListeners(): void {
    const searchInput = document.getElementById('model-search') as HTMLInputElement;
    const providerFilter = document.getElementById('provider-filter') as HTMLSelectElement;
    const refreshBtn = document.getElementById('refresh-btn');
    const addModelBtn = document.getElementById('add-model-btn');
    const modalClose = document.getElementById('modal-close');

    searchInput?.addEventListener('input', () => this.filterModels());
    providerFilter?.addEventListener('change', () => this.filterModels());
    refreshBtn?.addEventListener('click', () => this.loadModels());
    addModelBtn?.addEventListener('click', () => this.showModelForm(null));
    modalClose?.addEventListener('click', () => this.hideModelForm());
  }

  private async loadModels(): Promise<void> {
    this.loading = true;
    const container = document.getElementById('models-container');
    if (!container) return;

    container.innerHTML = '<div class="loading">⏳ Loading models...</div>';

    try {
      const response = await this.apiClient.get<{ models: Record<string, ModelConfig> }>('/v1/admin/config/models');

      // Convert object to array
      this.models = Object.values(response.data.models || {});
      this.filteredModels = [...this.models];
      this.renderModels();
    } catch (error) {
      container.innerHTML = `
        <div class="error">
          <p>❌ Failed to load models</p>
          <p class="error-detail">${error instanceof Error ? this.escapeHtml(error.message) : 'Unknown error'}</p>
        </div>
      `;
    } finally {
      this.loading = false;
    }
  }

  private filterModels(): void {
    const searchInput = document.getElementById('model-search') as HTMLInputElement;
    const providerFilter = document.getElementById('provider-filter') as HTMLSelectElement;

    const searchTerm = searchInput?.value.toLowerCase() || '';
    const providerValue = providerFilter?.value || '';

    this.filteredModels = this.models.filter(model => {
      const matchesSearch = !searchTerm ||
        model.id.toLowerCase().includes(searchTerm) ||
        model.display_name.toLowerCase().includes(searchTerm) ||
        model.provider.toLowerCase().includes(searchTerm);

      const matchesProvider = !providerValue || model.provider === providerValue;

      return matchesSearch && matchesProvider;
    });

    this.renderModels();
  }

  private renderModels(): void {
    const container = document.getElementById('models-container');
    if (!container) return;

    if (this.filteredModels.length === 0) {
      container.innerHTML = '<div class="no-results">No models found</div>';
      return;
    }

    container.innerHTML = `
      <div class="models-stats">
        <span>📊 Showing ${this.filteredModels.length} of ${this.models.length} models</span>
      </div>
      <div class="models-grid">
        ${this.filteredModels.map(model => this.renderModelCard(model)).join('')}
      </div>
    `;

    // Attach event listeners to action buttons
    this.filteredModels.forEach(model => {
      document.getElementById(`edit-${model.id}`)?.addEventListener('click', () => this.showModelForm(model));
      document.getElementById(`delete-${model.id}`)?.addEventListener('click', () => this.deleteModel(model));
    });
  }

  private renderModelCard(model: ModelConfig): string {
    const costScore = model.performance.cost_score;
    const qualityScore = model.performance.quality_score;

    return `
      <div class="model-card" data-model-id="${model.id}">
        <div class="model-card-header">
          <div class="model-title">
            <h4>${this.escapeHtml(model.display_name)}</h4>
            <span class="provider-badge ${this.escapeHtml(model.provider)}">${this.escapeHtml(model.provider)}</span>
          </div>
          <div class="model-actions">
            <button id="edit-${model.id}" class="icon-btn" title="Edit">✏️</button>
            <button id="delete-${model.id}" class="icon-btn" title="Delete">🗑️</button>
          </div>
        </div>

        <div class="model-card-body">
          <div class="model-info-row">
            <span class="label">ID:</span>
            <code>${model.id}</code>
          </div>
          <div class="model-info-row">
            <span class="label">Model:</span>
            <code class="truncate">${model.model_id}</code>
          </div>

          <div class="model-capabilities">
            <span class="capability ${model.capabilities.reasoning}">💡 ${model.capabilities.reasoning}</span>
            <span class="capability ${model.capabilities.coding}">💻 ${model.capabilities.coding}</span>
            ${model.capabilities.multilingual ? '<span class="capability">🌐 Multilingual</span>' : ''}
            ${model.china_friendly ? '<span class="capability">🇨🇳 China</span>' : ''}
          </div>

          <div class="model-metrics">
            <div class="metric">
              <span class="metric-label">Cost</span>
              <div class="metric-bar">
                <div class="metric-fill cost" style="width: ${costScore * 10}%"></div>
              </div>
              <span class="metric-value">${costScore}/10</span>
            </div>
            <div class="metric">
              <span class="metric-label">Quality</span>
              <div class="metric-bar">
                <div class="metric-fill quality" style="width: ${qualityScore * 10}%"></div>
              </div>
              <span class="metric-value">${qualityScore}/10</span>
            </div>
          </div>

          <div class="model-pricing">
            <span>💰 $${(model.cost.input_per_1m / 1000).toFixed(3)}/1K in</span>
            <span>$${(model.cost.output_per_1m / 1000).toFixed(3)}/1K out</span>
          </div>

          <div class="model-footer">
            <span class="model-status ${model.production_ready ? 'ready' : 'beta'}">
              ${model.production_ready ? '✅ Production' : '🧪 Beta'}
            </span>
            <span class="model-context">${(model.context_window / 1000).toFixed(0)}K ctx</span>
          </div>
        </div>
      </div>
    `;
  }

  private showModelForm(model: ModelConfig | null): void {
    this.editingModel = model;
    const modal = document.getElementById('model-form-modal');
    const modalTitle = document.getElementById('modal-title');
    const form = document.getElementById('model-form');

    if (!modal || !modalTitle || !form) return;

    modalTitle.textContent = model ? 'Edit Model' : 'Add Model';

    form.innerHTML = `
      <div class="form-section">
        <h4>Basic Information</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Model ID *</label>
            <input type="text" name="id" value="${model?.id || ''}"
              ${model ? 'readonly' : ''} required
              placeholder="e.g., azure-openai/gpt-4o" />
          </div>
          <div class="form-group">
            <label>Provider *</label>
            <select name="provider" required>
              <option value="unified" ${model?.provider === 'unified' ? 'selected' : ''}>Unified</option>
              <option value="deepseek" ${model?.provider === 'deepseek' ? 'selected' : ''}>DeepSeek</option>
              <option value="bedrock" ${model?.provider === 'bedrock' ? 'selected' : ''}>Bedrock</option>
              <option value="workers-ai" ${model?.provider === 'workers-ai' ? 'selected' : ''}>Workers AI</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label>Display Name *</label>
            <input type="text" name="display_name" value="${model?.display_name || ''}" required />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group full-width">
            <label>API Model ID *</label>
            <input type="text" name="model_id" value="${model?.model_id || ''}" required
              placeholder="e.g., azure-openai/gpt-4o or @cf/meta/llama-3.1-8b" />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Cost & Performance</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Input Cost (per 1M tokens) *</label>
            <input type="number" name="cost_input" value="${model?.cost.input_per_1m || 0}" required step="0.01" />
          </div>
          <div class="form-group">
            <label>Output Cost (per 1M tokens) *</label>
            <input type="number" name="cost_output" value="${model?.cost.output_per_1m || 0}" required step="0.01" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Quality Score (1-10) *</label>
            <input type="number" name="quality_score" value="${model?.performance.quality_score || 5}"
              required min="1" max="10" />
          </div>
          <div class="form-group">
            <label>Cost Score (1-10) *</label>
            <input type="number" name="cost_score" value="${model?.performance.cost_score || 5}"
              required min="1" max="10" />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Avg Latency (ms) *</label>
            <input type="number" name="avg_latency_ms" value="${model?.performance.avg_latency_ms || 1000}"
              required min="0" />
          </div>
          <div class="form-group">
            <label>Context Window *</label>
            <input type="number" name="context_window" value="${model?.context_window || 8192}"
              required min="1024" />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Capabilities</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Reasoning</label>
            <select name="reasoning">
              <option value="high" ${model?.capabilities.reasoning === 'high' ? 'selected' : ''}>High</option>
              <option value="medium" ${model?.capabilities.reasoning === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="low" ${model?.capabilities.reasoning === 'low' ? 'selected' : ''}>Low</option>
            </select>
          </div>
          <div class="form-group">
            <label>Coding</label>
            <select name="coding">
              <option value="high" ${model?.capabilities.coding === 'high' ? 'selected' : ''}>High</option>
              <option value="medium" ${model?.capabilities.coding === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="low" ${model?.capabilities.coding === 'low' ? 'selected' : ''}>Low</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label>Chinese Quality</label>
            <select name="chinese_quality">
              <option value="high" ${model?.capabilities.chinese_quality === 'high' ? 'selected' : ''}>High</option>
              <option value="medium" ${model?.capabilities.chinese_quality === 'medium' ? 'selected' : ''}>Medium</option>
              <option value="low" ${model?.capabilities.chinese_quality === 'low' ? 'selected' : ''}>Low</option>
            </select>
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" name="multilingual" ${model?.capabilities.multilingual ? 'checked' : ''} />
              Multilingual Support
            </label>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Settings</h4>
        <div class="form-row">
          <div class="form-group">
            <label>
              <input type="checkbox" name="china_friendly" ${model?.china_friendly ? 'checked' : ''} />
              Works in China
            </label>
          </div>
          <div class="form-group">
            <label>
              <input type="checkbox" name="production_ready" ${model?.production_ready ? 'checked' : ''} />
              Production Ready
            </label>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" class="btn btn-secondary" id="cancel-btn">Cancel</button>
        <button type="submit" class="btn btn-primary">${model ? 'Update' : 'Create'} Model</button>
      </div>
    `;

    // Setup form handlers
    form.addEventListener('submit', (e) => this.handleFormSubmit(e));
    document.getElementById('cancel-btn')?.addEventListener('click', () => this.hideModelForm());

    modal.style.display = 'flex';
  }

  private hideModelForm(): void {
    const modal = document.getElementById('model-form-modal');
    if (modal) modal.style.display = 'none';
    this.editingModel = null;
  }

  private async handleFormSubmit(e: Event): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const modelData: ModelConfig = {
      id: formData.get('id') as string,
      provider: formData.get('provider') as string,
      model_id: formData.get('model_id') as string,
      display_name: formData.get('display_name') as string,
      languages: ['en', 'ja', 'zh', 'ko'], // Default
      capabilities: {
        reasoning: formData.get('reasoning') as any,
        coding: formData.get('coding') as any,
        multilingual: formData.get('multilingual') === 'on',
        chinese_quality: formData.get('chinese_quality') as any,
      },
      restrictions: {
        blocked_countries: [],
      },
      cost: {
        input_per_1m: parseFloat(formData.get('cost_input') as string),
        output_per_1m: parseFloat(formData.get('cost_output') as string),
      },
      performance: {
        avg_latency_ms: parseInt(formData.get('avg_latency_ms') as string),
        quality_score: parseInt(formData.get('quality_score') as string),
        cost_score: parseInt(formData.get('cost_score') as string),
      },
      context_window: parseInt(formData.get('context_window') as string),
      china_friendly: formData.get('china_friendly') === 'on',
      production_ready: formData.get('production_ready') === 'on',
    };

    try {
      // Use the existing PUT endpoint with modelId and config structure
      await this.apiClient.put('/v1/admin/config/models', {
        modelId: modelData.id,
        config: modelData
      });

      alert(this.editingModel ? '✅ Model updated successfully!' : '✅ Model created successfully!');
      this.hideModelForm();
      await this.loadModels();
    } catch (error) {
      alert(`❌ Failed to save model: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private async deleteModel(model: ModelConfig): Promise<void> {
    if (!confirm(`Delete model "${model.display_name}" from KV?\n\nNote: This only removes the KV override. The hardcoded model will still be available.`)) {
      return;
    }

    try {
      await this.apiClient.delete(`/v1/admin/config/models/${encodeURIComponent(model.id)}`);
      alert('✅ Model deleted from KV successfully!');
      await this.loadModels();
    } catch (error) {
      alert(`❌ Failed to delete model: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  destroy(): void {
    this.container.innerHTML = '';
  }
}
